/**
 * ARVEXIS Live Voice Mode
 * ========================
 * 
 * A dedicated immersive voice conversation experience.
 * 
 * When the user enters Live Voice Mode:
 * - The interface becomes minimal and focused
 * - The sphere becomes the center of attention
 * - Conversation feels natural and uninterrupted
 * - Like talking with Arvexis in real time
 * 
 * This is not simply voice input — it's the feeling of 
 * having a live spoken conversation with Arvexis.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, X, Volume2, VolumeX } from 'lucide-react';
import ArvexisPresence from './ArvexisPresence';
import { ARVEXIS_VOLUME } from '../utils/arvexisAudio';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Arvexis's canonical voice
const ARVEXIS_VOICE = {
  voice: 'coral',
  speed: 0.88,
};

// Conversation states
const STATE = {
  LISTENING: 'listening',       // User is speaking
  PROCESSING: 'processing',     // Processing user speech
  SPEAKING: 'speaking',         // Arvexis is speaking
  WAITING: 'waiting',           // Waiting for user to speak
};

// =============================================================================
// VOICE WAVE RINGS — Visible animated waves during speech
// =============================================================================
const VoiceWaveRings = ({ isActive }) => {
  if (!isActive) return null;
  
  const rings = [
    { delay: 0, duration: 1.4 },
    { delay: 0.35, duration: 1.4 },
    { delay: 0.7, duration: 1.4 },
    { delay: 1.05, duration: 1.4 },
  ];
  
  return (
    <>
      {rings.map((ring, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            inset: '12%',
            border: '2px solid rgba(255, 215, 100, 0.6)',
            boxShadow: '0 0 12px rgba(255, 200, 80, 0.4), inset 0 0 12px rgba(255, 200, 80, 0.2)',
          }}
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{
            scale: [0.85, 1.3, 1.6],
            opacity: [0, 0.7, 0],
          }}
          transition={{
            duration: ring.duration,
            delay: ring.delay,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />
      ))}
    </>
  );
};

// =============================================================================
// INNER PULSE WAVES — Rhythmic energy inside the sphere
// =============================================================================
const InnerPulseWaves = ({ isSpeaking }) => {
  const pulseConfig = isSpeaking 
    ? { duration: 0.6, scale: [0.7, 1.1, 0.8, 1.05, 0.7], opacity: [0.3, 0.8, 0.4, 0.7, 0.3] }
    : { duration: 3, scale: [0.9, 1.02, 0.9], opacity: [0.15, 0.25, 0.15] };
  
  return (
    <>
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: '22%',
          background: isSpeaking
            ? 'radial-gradient(circle, rgba(255, 230, 150, 0.7) 0%, rgba(255, 200, 80, 0.4) 50%, transparent 100%)'
            : 'radial-gradient(circle, rgba(255, 250, 240, 0.3) 0%, rgba(212, 168, 67, 0.15) 50%, transparent 100%)',
          filter: 'blur(4px)',
        }}
        animate={{
          scale: pulseConfig.scale,
          opacity: pulseConfig.opacity,
        }}
        transition={{
          duration: pulseConfig.duration,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: '32%',
          background: isSpeaking
            ? 'radial-gradient(circle, rgba(255, 245, 200, 0.9) 0%, rgba(255, 215, 100, 0.5) 40%, transparent 100%)'
            : 'radial-gradient(circle, rgba(255, 255, 252, 0.25) 0%, rgba(212, 168, 67, 0.1) 40%, transparent 100%)',
          filter: 'blur(2px)',
        }}
        animate={{
          scale: isSpeaking ? [0.8, 1.15, 0.9, 1.1, 0.8] : [0.95, 1.01, 0.95],
          opacity: isSpeaking ? [0.4, 0.9, 0.5, 0.85, 0.4] : [0.2, 0.3, 0.2],
        }}
        transition={{
          duration: isSpeaking ? 0.5 : 4,
          delay: isSpeaking ? 0.15 : 0,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </>
  );
};

// =============================================================================
// LIVE SPHERE — The center of the voice experience
// =============================================================================
// Enhanced expressiveness: visible wave rings emanate during speech,
// colors shift to warmer gold, internal pulses beat rhythmically.
// Smooth transitions between states.
const LiveSphere = ({ state, onTap }) => {
  const isListening = state === STATE.LISTENING;
  const isSpeaking = state === STATE.SPEAKING;
  const isProcessing = state === STATE.PROCESSING;
  
  const getConfig = () => {
    if (isListening) {
      return {
        outerGlow: 0.7,
        innerLight: 0.65,
        coreIntensity: 0.8,
        pulseScale: [1, 1.04, 1],
        pulseDuration: 1.5,
        flowSpeed: 8,
        glowColor: 'rgba(212, 180, 100, 0.3)',
      };
    }
    if (isSpeaking) {
      return {
        // Maximum warmth and brightness during speech
        outerGlow: 1.0,
        innerLight: 0.95,
        coreIntensity: 1.0,
        pulseScale: [1, 1.04, 1.02, 1.035, 1],
        pulseDuration: 0.8,  // Faster breathing during speech
        flowSpeed: 4,
        glowColor: 'rgba(255, 215, 120, 0.5)',
      };
    }
    if (isProcessing) {
      return {
        outerGlow: 0.6,
        innerLight: 0.55,
        coreIntensity: 0.65,
        pulseScale: [1, 1.02, 1],
        pulseDuration: 2,
        flowSpeed: 10,
        glowColor: 'rgba(212, 168, 67, 0.25)',
      };
    }
    // Waiting
    return {
      outerGlow: 0.5,
      innerLight: 0.45,
      coreIntensity: 0.5,
      pulseScale: [1, 1.015, 1],
      pulseDuration: 4,
      flowSpeed: 15,
      glowColor: 'rgba(212, 168, 67, 0.2)',
    };
  };
  
  const config = getConfig();
  
  return (
    <motion.div
      className="relative cursor-pointer"
      style={{ width: 240, height: 240 }}
      onClick={onTap}
      whileTap={{ scale: 0.97 }}
    >
      {/* Breathing animation */}
      <motion.div
        className="absolute inset-0"
        animate={{ scale: config.pulseScale }}
        transition={{
          duration: config.pulseDuration,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        {/* Outer atmospheric glow — responds to speaking state */}
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{
            inset: '-50%',
            filter: 'blur(50px)',
          }}
          animate={{
            background: isSpeaking
              ? 'radial-gradient(circle, rgba(255, 220, 130, 0.45) 0%, rgba(255, 190, 80, 0.22) 40%, rgba(180, 140, 100, 0.1) 55%, transparent 70%)'
              : 'radial-gradient(circle, rgba(212, 168, 67, 0.25) 0%, rgba(180, 140, 100, 0.12) 40%, rgba(128, 90, 160, 0.06) 55%, transparent 70%)',
            opacity: [config.outerGlow * 0.85, config.outerGlow, config.outerGlow * 0.9],
            scale: isSpeaking ? [1, 1.08, 1.03, 1.06, 1] : [1, 1.04, 1],
          }}
          transition={{ 
            duration: isSpeaking ? 0.6 : config.pulseDuration * 0.8, 
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        
        {/* Flowing inner light — faster and brighter during speech */}
        <motion.div
          className="absolute rounded-full pointer-events-none overflow-hidden"
          style={{ inset: '5%' }}
        >
          <motion.div
            className="absolute inset-0"
            animate={{ rotate: 360 }}
            transition={{
              duration: config.flowSpeed,
              repeat: Infinity,
              ease: 'linear',
            }}
            style={{
              background: isSpeaking
                ? `conic-gradient(from 0deg,
                    rgba(255, 245, 200, 0.6) 0deg,
                    rgba(255, 215, 100, 0.4) 90deg,
                    rgba(255, 200, 80, 0.3) 180deg,
                    rgba(255, 245, 200, 0.55) 270deg,
                    rgba(255, 215, 100, 0.45) 360deg
                  )`
                : `conic-gradient(from 0deg,
                    rgba(255, 250, 240, ${config.innerLight * 0.4}) 0deg,
                    rgba(212, 168, 67, ${config.innerLight * 0.25}) 90deg,
                    rgba(180, 140, 100, ${config.innerLight * 0.18}) 180deg,
                    rgba(255, 250, 240, ${config.innerLight * 0.35}) 270deg,
                    rgba(212, 168, 67, ${config.innerLight * 0.3}) 360deg
                  )`,
              filter: 'blur(30px)',
              mixBlendMode: 'screen',
            }}
          />
          
          <motion.div
            className="absolute inset-[20%]"
            animate={{ rotate: -360 }}
            transition={{
              duration: config.flowSpeed * 1.3,
              repeat: Infinity,
              ease: 'linear',
            }}
            style={{
              background: isSpeaking
                ? `conic-gradient(from 180deg,
                    rgba(255, 250, 220, 0.65) 0deg,
                    transparent 120deg,
                    rgba(255, 215, 100, 0.5) 240deg,
                    rgba(255, 250, 220, 0.6) 360deg
                  )`
                : `conic-gradient(from 180deg,
                    rgba(255, 252, 248, ${config.innerLight * 0.45}) 0deg,
                    transparent 120deg,
                    rgba(212, 168, 67, ${config.innerLight * 0.35}) 240deg,
                    rgba(255, 252, 248, ${config.innerLight * 0.4}) 360deg
                  )`,
              filter: 'blur(20px)',
              mixBlendMode: 'screen',
            }}
          />
        </motion.div>
        
        {/* Inner pulse waves — rhythmic energy during speech */}
        <InnerPulseWaves isSpeaking={isSpeaking} />
        
        {/* Core light — the source of voice, brightest during speech */}
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{ inset: '28%' }}
          animate={{
            opacity: isSpeaking 
              ? [0.8, 1, 0.85, 0.95, 0.8] 
              : [config.coreIntensity * 0.75, config.coreIntensity, config.coreIntensity * 0.85],
            scale: isSpeaking 
              ? [0.92, 1.08, 0.96, 1.04, 0.92] 
              : [0.96, 1, 0.98],
          }}
          transition={{
            duration: isSpeaking ? 0.5 : config.pulseDuration * 0.4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{
              background: isSpeaking
                ? 'radial-gradient(circle, rgba(255, 255, 240, 1) 0%, rgba(255, 240, 200, 0.85) 35%, rgba(255, 215, 100, 0.5) 65%, transparent 100%)'
                : `radial-gradient(circle, rgba(255, 255, 252, ${config.coreIntensity * 0.75}) 0%, rgba(255, 250, 240, ${config.coreIntensity * 0.5}) 35%, rgba(212, 168, 67, ${config.coreIntensity * 0.3}) 65%, transparent 100%)`,
            }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            style={{ filter: 'blur(4px)' }}
          />
        </motion.div>
        
        {/* Voice wave rings — visible during speech */}
        <VoiceWaveRings isActive={isSpeaking} />
        
        {/* The Arvexis presence */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{
            filter: isSpeaking
              ? 'drop-shadow(0 0 60px rgba(255, 200, 100, 0.6)) drop-shadow(0 0 30px rgba(255, 220, 150, 0.4))'
              : `drop-shadow(0 0 ${45 + config.outerGlow * 45}px rgba(140, 100, 180, ${0.22 + config.outerGlow * 0.2}))`,
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <ArvexisPresence size="large" />
        </motion.div>
      </motion.div>
      
      {/* Listening indicator ring */}
      {isListening && (
        <motion.div
          className="absolute inset-[-15%] rounded-full pointer-events-none"
          style={{
            border: '2px solid rgba(212, 168, 67, 0.5)',
          }}
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.6, 0.8, 0.6],
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}
    </motion.div>
  );
};

// =============================================================================
// STATUS INDICATOR
// =============================================================================
const StatusIndicator = ({ state, lang }) => {
  const getText = () => {
    switch (state) {
      case STATE.LISTENING:
        return lang === 'es' ? 'Escuchando...' : 'Listening...';
      case STATE.PROCESSING:
        return lang === 'es' ? 'Pensando...' : 'Thinking...';
      case STATE.SPEAKING:
        return lang === 'es' ? 'Arvexis está hablando' : 'Arvexis is speaking';
      default:
        return lang === 'es' ? 'Toca para hablar' : 'Tap to speak';
    }
  };
  
  return (
    <motion.p
      key={state}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -5 }}
      className="text-center"
      style={{
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        fontSize: '1rem',
        color: 'rgba(255, 255, 255, 0.6)',
        fontStyle: 'italic',
      }}
    >
      {getText()}
    </motion.p>
  );
};

// =============================================================================
// MAIN COMPONENT — Live Voice Mode
// =============================================================================
const LiveVoiceMode = ({ 
  lang = 'en', 
  onClose,
  sessionId,
}) => {
  const [state, setState] = useState(STATE.WAITING);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);
  const [lastTranscript, setLastTranscript] = useState('');
  const [voiceMuted, setVoiceMuted] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);
  
  // Handle sphere tap — toggle listening
  const handleSphereTap = () => {
    if (state === STATE.LISTENING) {
      stopListening();
    } else if (state === STATE.WAITING) {
      startListening();
    } else if (state === STATE.SPEAKING && audioRef.current) {
      // Interrupt speech
      audioRef.current.pause();
      audioRef.current = null;
      setState(STATE.WAITING);
    }
  };
  
  // Start listening for voice input — Using SAME Whisper pipeline as Dream Recorder
  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Use the same audio format detection as Dream Recorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        stream.getTracks().forEach(track => track.stop());
        await processVoiceInput(audioBlob);
      };
      
      mediaRecorderRef.current.start(1000); // Collect data every second like Dream Recorder
      setState(STATE.LISTENING);
    } catch (error) {
      console.error('Microphone error:', error);
    }
  };
  
  // Stop listening
  const stopListening = () => {
    if (mediaRecorderRef.current && state === STATE.LISTENING) {
      mediaRecorderRef.current.stop();
      setState(STATE.PROCESSING);
    }
  };
  
  // Process voice input — transcribe using EXACT same Whisper pipeline as Dream Recorder
  const processVoiceInput = async (audioBlob) => {
    try {
      // Transcribe using the same field name and filename as Dream Recorder
      const formData = new FormData();
      // CRITICAL: Use 'file' field name with filename, exactly like Dream Recorder
      formData.append('file', audioBlob, 'recording.webm');
      
      // Transcription now requires a session; a voice session easily outlives the 15-minute
      // access token, so refresh once on 401 instead of dropping the turn.
      const postTranscribe = () => fetch(`${API_URL}/api/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      let transcribeResponse = await postTranscribe();
      if (transcribeResponse.status === 401) {
        const refreshed = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include'
        });
        if (refreshed.ok) transcribeResponse = await postTranscribe();
      }
      
      if (!transcribeResponse.ok) throw new Error('Transcription failed');
      
      const transcribeData = await transcribeResponse.json();
      const userText = transcribeData.text;
      
      if (!userText?.trim()) {
        setState(STATE.WAITING);
        return;
      }
      
      setLastTranscript(userText);
      
      // Send to Arvexis
      const chatResponse = await fetch(`${API_URL}/api/companion/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: userText,
          session_id: currentSessionId,
          surface: 'live_voice',
          language: lang,
        }),
      });
      
      if (!chatResponse.ok) throw new Error('Chat failed');
      
      const chatData = await chatResponse.json();
      
      if (chatData.session_id) {
        setCurrentSessionId(chatData.session_id);
      }
      
      // Speak response
      await speakResponse(chatData.message);
      
    } catch (error) {
      console.error('Voice processing error:', error);
      setState(STATE.WAITING);
    }
  };
  
  // Speak Arvexis's response
  const speakResponse = async (text) => {
    if (!isMountedRef.current) return;
    
    setState(STATE.SPEAKING);
    
    if (voiceMuted) {
      // Just wait proportional to text length
      await new Promise(resolve => setTimeout(resolve, 1000 + text.length * 50));
      if (isMountedRef.current) setState(STATE.WAITING);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/companion/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          text,
          voice: 'auto',  // Auto-detect language for best voice
          speed: ARVEXIS_VOICE.speed,
          language: lang,  // Pass user's language preference
        }),
      });
      
      if (!response.ok) throw new Error('TTS failed');
      
      const data = await response.json();
      
      if (data.audio && isMountedRef.current) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
        audio.volume = ARVEXIS_VOLUME;
        audioRef.current = audio;
        
        audio.onended = () => {
          if (isMountedRef.current) {
            setState(STATE.WAITING);
          }
        };
        
        audio.onerror = () => {
          if (isMountedRef.current) {
            setState(STATE.WAITING);
          }
        };
        
        await audio.play();
      }
    } catch (error) {
      console.error('TTS error:', error);
      if (isMountedRef.current) setState(STATE.WAITING);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] flex flex-col"
      style={{
        background: 'linear-gradient(180deg, #050308 0%, #080610 40%, #0a0814 70%, #060409 100%)',
      }}
      data-testid="live-voice-mode"
    >
      {/* Header — minimal */}
      <header 
        className="flex items-center justify-between px-4"
        style={{ 
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
          paddingBottom: '16px',
        }}
      >
        <button
          onClick={onClose}
          className="p-2 rounded-full"
          style={{ background: 'rgba(255,255,255,0.06)' }}
          data-testid="live-voice-close-btn"
        >
          <X size={22} className="text-white/60" />
        </button>
        
        <p
          className="text-white/50"
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: '1rem',
          }}
        >
          {lang === 'es' ? 'Modo voz' : 'Voice Mode'}
        </p>
        
        <button
          onClick={() => setVoiceMuted(!voiceMuted)}
          className="p-2 rounded-full"
          style={{ background: voiceMuted ? 'rgba(255,255,255,0.06)' : 'rgba(212, 168, 67, 0.15)' }}
        >
          {voiceMuted 
            ? <VolumeX size={20} className="text-white/40" />
            : <Volume2 size={20} className="text-[#D4AF37]/80" />
          }
        </button>
      </header>
      
      {/* Main stage — sphere centered */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <LiveSphere state={state} onTap={handleSphereTap} />
        
        <div className="mt-12">
          <AnimatePresence mode="wait">
            <StatusIndicator key={state} state={state} lang={lang} />
          </AnimatePresence>
        </div>
        
        {/* Last transcript — subtle display */}
        {lastTranscript && state !== STATE.LISTENING && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            className="mt-8 px-8 text-center max-w-sm"
            style={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.85rem',
              color: 'rgba(255,255,255,0.4)',
            }}
          >
            &ldquo;{lastTranscript}&rdquo;
          </motion.p>
        )}
      </div>
      
      {/* Bottom hint */}
      <div 
        className="pb-8 text-center"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)' }}
      >
        <p
          className="text-white/25"
          style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.75rem',
          }}
        >
          {state === STATE.LISTENING 
            ? (lang === 'es' ? 'Toca cuando termines' : 'Tap when done')
            : (lang === 'es' ? 'Toca la esfera para hablar' : 'Tap the sphere to speak')
          }
        </p>
      </div>
    </motion.div>
  );
};

export default LiveVoiceMode;
